// src/pages/Payment/PaymentPage.jsx

import React, { useEffect, useRef, useState } from "react";
import {
    CheckCircle,
    CreditCard as PaymentCardIcon,
    XCircle,
    Tag as TagIcon,
    MinusCircle,
    PlayCircle,
    Hourglass,
    Check,
    Rocket,
} from "lucide-react";
import { Modal, Form, InputNumber, Button, Spin, Input, Space, Typography, Switch, message, Tooltip, Pagination } from "antd";
import paymentApi from "../../api/paymentApi";
import feeApi from "../../api/feeApi";
import blockchainApi from "../../api/blockchainApi";
import promosApiExports from "../../api/promosApi";
import CoinBadge from "../../components/coin_badge/CoinBadge";
import "../../components/AddUserModal/style.css";

const promosUserApi = promosApiExports.userPromos;

const { Text } = Typography;

const IMAGE_BASE_URL = "https://api.duotetra.quest/uploads/gems/";

function cn(...c) {
    return c.filter(Boolean).join(" ");
}

const shellClass =
    "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

const inputNumGlass =
    "w-full !rounded-xl [&_.ant-input-number-input]:!text-white/90 [&_.ant-input-number-handler-wrap]:!border-white/10";

const iconBtn =
    "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-white/80 transition hover:border-white/20 hover:bg-white/10";

export default function PaymentPage() {
    const [items, setItems] = useState([]);
    const [totalItems, setTotalItems] = useState(0);
    const [loading, setLoading] = useState(false);
    const [blockchainMap, setBlockchainMap] = useState({}); // promoId → { blockchainStatus, depositTxHash, feeTxHash }
    const [startingId, setStartingId] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const [discountModal, setDiscountModal] = useState(false);
    const [currentPromo, setCurrentPromo] = useState(null);
    const [form] = Form.useForm();

    const requestIdRef = useRef(0);

    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize) || 1);

    const calculateItemFee = async (item) => {
        if (item.isPaid) return item.feeAmount;
        return item.promoType === "onlyCrystals"
            ? await feeApi.calculateFeeGem(item.promoId, item.participantsCount)
            : await feeApi.calculateFee(item.promoId, item.participantsCount);
    };

    const fetchData = async () => {
        const myId = ++requestIdRef.current;
        setLoading(true);
        try {
            const { items: rawItems, total } = await paymentApi.getAllWithPromos({
                page: currentPage,
                pageSize,
            });
            if (myId !== requestIdRef.current) return;

            const enriched = await Promise.all(
                rawItems.map(async (row) => {
                    const feeAmount = await calculateItemFee(row);
                    const { discountId, discountPercentage, isApplied } = await feeApi.getDiscountStatus(row.promoId);
                    return {
                        ...row,
                        feeAmount,
                        discountId,
                        discountPercentage,
                        isApplied,
                    };
                })
            );
            if (myId !== requestIdRef.current) return;

            setItems(enriched);
            setTotalItems(total);

            // Параллельно загружаем blockchain статус для каждого промо
            const bcResults = await Promise.allSettled(
                rawItems.map(row =>
                    blockchainApi.getPromoBlockchainInfo(row.promoId)
                        .then(res => ({ promoId: row.promoId, ...(res?.data || res) }))
                        .catch(() => ({ promoId: row.promoId }))
                )
            );
            if (myId !== requestIdRef.current) return;

            const bcMap = {};
            for (const r of bcResults) {
                if (r.status === 'fulfilled' && r.value?.promoId) {
                    bcMap[r.value.promoId] = r.value;
                }
            }
            setBlockchainMap(bcMap);
        } catch (e) {
            console.error(e);
            if (myId === requestIdRef.current) {
                message.error("Не удалось загрузить данные");
            }
        } finally {
            if (myId === requestIdRef.current) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        setBlockchainMap({}); // сбрасываем чтобы не было устаревших данных при смене страницы
        fetchData();
    }, [currentPage, pageSize]);

    const handleStart = async (promoId) => {
        setStartingId(promoId);
        try {
            await promosUserApi.startPromo(promoId);
            message.success(`Промо #${promoId} запущено!`);
            fetchData();
        } catch (e) {
            message.error(e?.response?.data?.message || "Ошибка запуска промо");
        } finally {
            setStartingId(null);
        }
    };

    const handlePay = async (promoId) => {
        try {
            await paymentApi.createPayment({ promoId });
            message.success("Оплата проведена!");
            fetchData();
        } catch {
            message.error("Ошибка при оплате");
        }
    };

    const handleCancel = async (promoId) => {
        try {
            await paymentApi.cancelPayment(promoId);
            message.success("Оплата отменена!");
            fetchData();
        } catch {
            message.error("Ошибка при отмене оплаты");
        }
    };

    const handleApplyDiscount = async (discountId, promoId, discountPercentage) => {
        try {
            await feeApi.updateDiscount(discountId, {
                promoId,
                discountPercentage,
                isApplied: true,
            });
            message.success("Скидка применена!");
            fetchData();
        } catch {
            message.error("Не удалось применить скидку");
        }
    };

    const openDiscountModal = (promo) => {
        setCurrentPromo(promo);
        form.setFieldsValue({
            promoId: promo.promoId,
            discountPercentage: promo.discountPercentage ?? 0,
            isApplied: promo.isApplied,
        });
        setDiscountModal(true);
    };

    const handleDiscountSubmit = async ({ promoId, discountPercentage, isApplied }) => {
        try {
            if (currentPromo.discountId) {
                await feeApi.updateDiscount(currentPromo.discountId, {
                    promoId,
                    discountPercentage,
                    isApplied,
                });
                message.success("Скидка обновлена!");
            } else {
                await feeApi.createDiscount({ promoId, discountPercentage, isApplied });
                message.success("Скидка создана!");
            }
            setDiscountModal(false);
            fetchData();
        } catch {
            message.error("Ошибка при сохранении скидки");
        }
    };

    const handleDiscountRemove = async (discountId) => {
        try {
            await feeApi.deleteDiscount(discountId);
            message.success("Скидка удалена!");
            setDiscountModal(false);
            fetchData();
        } catch {
            message.error("Ошибка при удалении скидки");
        }
    };

    const renderStatus = (status) => {
        const base = "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium";
        switch (status) {
            case "в ожидании":
            case "PENDING":
                return (
                    <span className={cn(base, "border-amber-400/35 bg-amber-500/12 text-amber-100")}>
                        <Hourglass size={12} /> Ожидание
                    </span>
                );
            case "проводится":
            case "ACTIVE":
                return (
                    <span className={cn(base, "border-emerald-400/35 bg-emerald-500/12 text-emerald-100")}>
                        <PlayCircle size={12} /> Активно
                    </span>
                );
            case "завершился":
            case "COMPLETED":
            case "MANUALLY_COMPLETED":
                return (
                    <span className={cn(base, "border-sky-400/35 bg-sky-500/12 text-sky-100")}>
                        <CheckCircle size={12} /> Завершено
                    </span>
                );
            default:
                return (
                    <span className={cn(base, "border-white/15 bg-white/[0.08] text-white/65")}>{status}</span>
                );
        }
    };

    const renderPromoType = (promoType) => {
        switch (promoType) {
            case "onlyCrystals":
                return (
                    <span className="inline-flex rounded-full border border-violet-400/30 bg-violet-500/12 px-2 py-0.5 text-[11px] text-violet-100">
                        Crystals
                    </span>
                );
            case "crystalsAndCrypto":
                return (
                    <span className="inline-flex rounded-full border border-white/15 bg-white/[0.08] px-2 py-0.5 text-[11px] text-white/75">
                        Crypto+Crystals
                    </span>
                );
            case "cryptoOnly":
                return (
                    <span className="inline-flex rounded-full border border-amber-400/30 bg-amber-500/12 px-2 py-0.5 text-[11px] text-amber-100">
                        Crypto
                    </span>
                );
            default:
                return <span className="text-[11px] text-white/40">{promoType || "—"}</span>;
        }
    };

    const th = "whitespace-nowrap px-3 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wide text-white/50 sm:px-4";
    const td = "px-3 py-3 text-[12px] text-white/85 sm:px-4";

    if (loading && !items.length) {
        return (
            <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
                <section className={cn(shellClass, "flex min-h-[280px] flex-1 items-center justify-center p-12")}>
                    <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                </section>
            </div>
        );
    }

    return (
        <div className="flex min-h-[calc(100dvh-1.5rem)] flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-emerald-500/10 blur-3xl" />
                <div className="relative flex items-center gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                        <PaymentCardIcon className="h-5 w-5 text-emerald-300/90" strokeWidth={2} />
                    </span>
                    <div>
                        <h1 className="text-lg font-semibold tracking-tight text-white/95 sm:text-xl">Управление оплатами</h1>
                        <p className="text-[12px] text-white/45">Комиссии, скидки и статус оплаты по промо</p>
                    </div>
                </div>
            </section>

            <div
                className={cn(
                    shellClass,
                    "flex shrink-0 flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                )}
            >
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                    <span className="text-[13px] text-white/50">
                        Стр. {currentPage} из {totalPages}
                    </span>
                    <span className="text-[12px] text-white/35">
                        Всего записей: {totalItems} · на странице: {pageSize}
                    </span>
                </div>
                <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={totalItems}
                    showSizeChanger
                    pageSizeOptions={["10", "20", "50"]}
                    onChange={(page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    }}
                    className="pagination-custom !mb-0 flex flex-wrap justify-end"
                    size="small"
                />
            </div>

            <section className={cn(shellClass, "flex min-h-0 flex-1 flex-col overflow-hidden")}>
                <div className="relative min-h-0 flex-1">
                    {loading && items.length > 0 && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl bg-[#070A12]/45 backdrop-blur-sm">
                            <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                        </div>
                    )}
                    <div className="h-full min-h-[320px] overflow-auto">
                        <table className="w-full min-w-[1100px] border-collapse text-left">
                            <thead className="sticky top-0 z-[1] bg-[#0a0e18]/90 text-white/70 backdrop-blur-md">
                                <tr className="border-b border-white/10">
                                    <th className={th}>ID</th>
                                    <th className={th}>Тип</th>
                                    <th className={th}>Бюджет</th>
                                    <th className={th}>Статус</th>
                                    <th className={th}>Участн.</th>
                                    <th className={th}>Призов.</th>
                                    <th className={th}>Комисс.</th>
                                    <th className={th}>Оплач. (old)</th>
                                    <th className={th}>Депозит ⛓</th>
                                    <th className={th}>Fee ⛓</th>
                                    <th className={th}>Скидка</th>
                                    <th className={cn(th, "text-center")}>Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item) => {
                                    const positions = item.category?.positions || [];

                                    const crystalBudget =
                                        item.promoType === "onlyCrystals"
                                            ? (positions.reduce((sum, p) => {
                                                  const budget = Number(p?.budget);
                                                  if (!Number.isNaN(budget) && budget > 0) return sum + budget;
                                                  const winners = Number(p?.winners) || 0;
                                                  const drop = Number(p?.dropPerWinner) || 0;
                                                  return sum + winners * drop;
                                              }, 0) || item.totalAmount || 0)
                                            : null;

                                    return (
                                        <tr
                                            key={item.promoId}
                                            className="border-t border-white/[0.06] transition-colors hover:bg-white/[0.04]"
                                        >
                                            <td className={cn(td, "font-semibold text-emerald-300/90")}>{item.promoId}</td>
                                            <td className={td}>{renderPromoType(item.promoType)}</td>
                                            <td className={td}>
                                                {item.promoType === "onlyCrystals" ? (
                                                    <CoinBadge
                                                        count={crystalBudget}
                                                        icon={item.gemFrontImage ? `${IMAGE_BASE_URL}${item.gemFrontImage}` : null}
                                                        name="Crystals"
                                                    />
                                                ) : (
                                                    <CoinBadge count={item.totalAmount} name={item.promoTokenType} />
                                                )}
                                            </td>
                                            <td className={td}>{renderStatus(item.promoStatus)}</td>
                                            <td className={td}>{item.participantsCount}</td>
                                            <td className={td}>{item.prizePlacesCount}</td>
                                            <td className={cn(td, "font-semibold text-emerald-300/90")}>
                                                {item.feeAmount != null ? item.feeAmount.toFixed(2) : "—"}
                                            </td>
                                            <td className={td}>
                                                <span
                                                    className={cn(
                                                        "inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium",
                                                        item.isPaid
                                                            ? "border-emerald-400/35 bg-emerald-500/12 text-emerald-100"
                                                            : "border-white/12 bg-white/[0.06] text-white/45"
                                                    )}
                                                >
                                                    {item.isPaid ? "Да" : "Нет"}
                                                </span>
                                            </td>
                                            {/* Депозит on-chain */}
                                            <td className={td}>
                                                {(() => {
                                                    const bc = blockchainMap[item.promoId];
                                                    if (!bc?.depositTxHash) return <span className="text-white/30 text-[11px]">—</span>;
                                                    return (
                                                        <Tooltip title={bc.depositTxHash}>
                                                            <span className="inline-flex rounded-full border border-emerald-400/35 bg-emerald-500/12 px-2 py-0.5 text-[10px] text-emerald-200 cursor-default">
                                                                ✓ Paid
                                                            </span>
                                                        </Tooltip>
                                                    );
                                                })()}
                                            </td>
                                            {/* Fee on-chain */}
                                            <td className={td}>
                                                {(() => {
                                                    const bc = blockchainMap[item.promoId];
                                                    if (!bc?.feeTxHash) return <span className="text-white/30 text-[11px]">—</span>;
                                                    return (
                                                        <Tooltip title={bc.feeTxHash}>
                                                            <span className="inline-flex rounded-full border border-emerald-400/35 bg-emerald-500/12 px-2 py-0.5 text-[10px] text-emerald-200 cursor-default">
                                                                ✓ Paid
                                                            </span>
                                                        </Tooltip>
                                                    );
                                                })()}
                                            </td>
                                            <td className={td}>
                                                {item.discountPercentage != null ? (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <span className="font-semibold text-emerald-300/90">
                                                            {item.discountPercentage}%
                                                        </span>
                                                        {!item.isApplied && item.discountId && (
                                                            <Tooltip title="Применить скидку">
                                                                <button
                                                                    type="button"
                                                                    onClick={() =>
                                                                        handleApplyDiscount(
                                                                            item.discountId,
                                                                            item.promoId,
                                                                            item.discountPercentage
                                                                        )
                                                                    }
                                                                    className={cn(
                                                                        iconBtn,
                                                                        "!h-7 !w-7 border-emerald-400/30 bg-emerald-500/15 text-emerald-200 hover:border-emerald-400/50 hover:bg-emerald-500/25"
                                                                    )}
                                                                >
                                                                    <Check size={14} />
                                                                </button>
                                                            </Tooltip>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="text-white/35">—</span>
                                                )}
                                            </td>
                                            <td className={cn(td, "text-center")}>
                                                <div className="flex flex-wrap items-center justify-center gap-1">
                                                    {/* START button — если on-chain funded и промо ещё не ACTIVE */}
                                                    {(() => {
                                                        const bc = blockchainMap[item.promoId];
                                                        const isFunded = bc?.blockchainStatus === 'funded';
                                                        const isNotActive = item.promoStatus !== 'ACTIVE';
                                                        if (isFunded && isNotActive) {
                                                            return (
                                                                <Tooltip title="Запустить промо">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleStart(item.promoId)}
                                                                        disabled={startingId === item.promoId}
                                                                        className={cn(
                                                                            iconBtn,
                                                                            "border-green-400/35 bg-green-500/15 text-green-100 hover:border-green-400/55 hover:bg-green-500/25 disabled:opacity-50"
                                                                        )}
                                                                    >
                                                                        {startingId === item.promoId
                                                                            ? <div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
                                                                            : <Rocket size={14} />
                                                                        }
                                                                    </button>
                                                                </Tooltip>
                                                            );
                                                        }
                                                        return null;
                                                    })()}
                                                    {!item.isPaid ? (
                                                        <Tooltip title="Оплатить (старая система)">
                                                            <button
                                                                type="button"
                                                                onClick={() => handlePay(item.promoId)}
                                                                className={cn(
                                                                    iconBtn,
                                                                    "border-emerald-400/35 bg-emerald-500/15 text-emerald-100 hover:border-emerald-400/55 hover:bg-emerald-500/25"
                                                                )}
                                                            >
                                                                <PaymentCardIcon size={14} />
                                                            </button>
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Отменить оплату (старая система)">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleCancel(item.promoId)}
                                                                className={cn(
                                                                    iconBtn,
                                                                    "border-rose-400/35 bg-rose-500/15 text-rose-100 hover:border-rose-400/55 hover:bg-rose-500/25"
                                                                )}
                                                            >
                                                                <XCircle size={14} />
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title={item.discountId ? "Редактировать скидку" : "Создать скидку"}>
                                                        <button
                                                            type="button"
                                                            onClick={() => openDiscountModal(item)}
                                                            className={cn(
                                                                iconBtn,
                                                                "border-amber-400/35 bg-amber-500/15 text-amber-100 hover:border-amber-400/55 hover:bg-amber-500/25"
                                                            )}
                                                        >
                                                            <TagIcon size={14} />
                                                        </button>
                                                    </Tooltip>
                                                    {item.discountId && (
                                                        <Tooltip title="Удалить скидку">
                                                            <button
                                                                type="button"
                                                                onClick={() => handleDiscountRemove(item.discountId)}
                                                                className={cn(
                                                                    iconBtn,
                                                                    "border-white/12 text-white/55 hover:border-white/25 hover:bg-white/10"
                                                                )}
                                                            >
                                                                <MinusCircle size={14} />
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {items.length === 0 && !loading && (
                            <div className="flex flex-col items-center py-16 text-white/45">
                                <PaymentCardIcon className="mb-2 h-12 w-12 opacity-40" />
                                <p className="text-[13px]">Нет записей для отображения</p>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            <Modal
                open={discountModal}
                title={`Скидка для промо ${currentPromo?.promoId}`}
                onCancel={() => setDiscountModal(false)}
                footer={null}
                className="custom-modal"
                destroyOnClose
            >
                <Form form={form} layout="vertical" onFinish={handleDiscountSubmit}>
                    <Form.Item name="promoId" hidden>
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="discountPercentage"
                        label={<span className="text-white/70">Скидка (%)</span>}
                        rules={[{ required: true, message: "Введите % скидки" }]}
                    >
                        <InputNumber min={0} max={100} className={inputNumGlass} size="large" />
                    </Form.Item>
                    <Form.Item name="isApplied" valuePropName="checked">
                        <Space>
                            <Switch className="[&_.ant-switch-checked]:!bg-emerald-500" />
                            <Text className="text-white/80">Применить сразу</Text>
                        </Space>
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                        >
                            Сохранить
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
}
